// calcite.ts
// Helper factories for building calcite elements, imported in map-window custom element
export type actbarWithTooltips = { bar: HTMLCalciteActionBarElement, tooltips: HTMLCalciteTooltipElement[] };
export type calcitePanelProps = {
    elementType?: string,
    heading?: string,
    closable?: boolean,
    cssClass?: string,
};

// HELPER FOR BUIDING GENERIC CALCITE PANEL WITH THE PASSED ELEMENT AS ITS CHILD
export function buildCalcitePanel(props: calcitePanelProps): HTMLCalcitePanelElement {
    const panel = document.createElement("calcite-panel");
    if (props.heading) panel.heading = props.heading;
    panel.hidden = true;
    panel.closable = props.closable ?? true;
    if (props.cssClass) panel.classList.add(props.cssClass);
    if (props.elementType) {
        const content = document.createElement(props.elementType) as any;
        panel.appendChild(content);
    }
    return panel;
}

// tooltips
type tooltipPlacements = "left" | "right" | "auto" | "top" | "auto-start" | "auto-end" | "top-start" |
    "top-end" | "bottom" | "bottom-start" | "bottom-end" | "right-start" | "right-end" | "left-start" |
    "left-end" | "leading-start" | "leading" | "leading-end" | "trailing-end" | "trailing" | "trailing-start";
export type calciteTooltipProps = {
    text: string,
    refElId?: string,
    placement?: tooltipPlacements,
};
export function buildCalciteTooltip(props: calciteTooltipProps): HTMLCalciteTooltipElement {
    const tooltip = document.createElement("calcite-tooltip");
    tooltip.referenceElement = props.refElId ?? "";
    tooltip.textContent = props.text;
    tooltip.placement = props.placement ?? "bottom";
    tooltip.overlayPositioning = "fixed";
    return tooltip;
};

// BUILD A CALCITE ACTION
export type calciteActionProps = {
    id: string,
    icon: string,
    text: string,
    where?: string,
    highlightName?: string,
    onClick?: () => Promise<void>,
    onBuilt?: (action: HTMLCalciteActionElement) => void,
    tooltipProps?: calciteTooltipProps,
}
// return action with or without a tooltip. tooltip must be appended to the root as well
export type calciteActionReturn = {
    action: HTMLCalciteActionElement,
    tooltip: HTMLCalciteTooltipElement | null,
}
export function buildCalciteAction(props: calciteActionProps): calciteActionReturn { 
    const btnId = `toggle-action-${props.id}`;
    const action = Object.assign(document.createElement('calcite-action'), {
        id: btnId,
        icon: props.icon,
        text: props.text,
    })
    action.dataset['actionId'] = props.id;
    if (props.onClick) {
        action.addEventListener("click", props.onClick);
    }
    const tooltip = props.tooltipProps ? buildCalciteTooltip(props.tooltipProps) : null;
    if (props.tooltipProps && tooltip) {
        tooltip.referenceElement = btnId;
    }
    props.onBuilt?.(action);
    return { action, tooltip }
};

// BUILD AN ACTION BAR

export type calciteActionBarProps = {
    layout?: string;
    cssClass?: string;
    expandable?: boolean;
    actions?: HTMLCalciteActionElement[];
    actionsProps?: calciteActionProps[];
    hideBtn?: calciteActionProps | boolean;
}
export type calciteActionBarWithTooltips = {
    actionBar: HTMLCalciteActionBarElement;
    tooltips: HTMLCalciteTooltipElement[] | null;
};
export function buildCalciteActionBar(props: calciteActionBarProps): HTMLCalciteActionBarElement {
    const actionBar = Object.assign(document.createElement("calcite-action-bar"), {
        layout: props.layout ?? 'horizontal',
        expandDisabled: !(props.expandable ?? false),
    });
    if (props.cssClass) actionBar.classList.add(props.cssClass);
    return actionBar;
}

export const defaultHideId = 'hide-all';
export const defaultHideBtnId = `toggle-action-${defaultHideId}`;
export function buildCalciteActionBarWithActions(props: calciteActionBarProps): calciteActionBarWithTooltips {
    const actionBar = Object.assign(document.createElement("calcite-action-bar"), {
        layout: props.layout ?? 'horizontal',
        expandDisabled: !(props.expandable ?? false),
    });
    if (props.cssClass) actionBar.classList.add(props.cssClass);

    const actions: HTMLCalciteActionElement[] = [];
    const tooltips: HTMLCalciteTooltipElement[] = [];

    if (props.actionsProps) {
        props.actionsProps.forEach((p: calciteActionProps) => {
            const { action, tooltip } = buildCalciteAction(p);
            actions.push(action);
            if (tooltip) tooltips.push(tooltip);
        });
    }

    if (props.hideBtn) {
        const defTxt = 'Hide Action Bar';
        const defaultHideProps: calciteActionProps = {
            id: defaultHideId,
            icon: defaultActbarIcons.hide,
            text: defTxt,
            tooltipProps: { text: defTxt, placement: 'auto' },
            onClick: async () => hideActionBar(actionBar),
        };
        const hideProps = typeof props.hideBtn === 'object' ? props.hideBtn : defaultHideProps;
        const { action: hideAct, tooltip: hideTT } = buildCalciteAction(hideProps);
        actions.push(hideAct);
        if (hideTT) tooltips.push(hideTT);
    }

    actionBar.append(...actions ?? props.actions ?? null);
    return { actionBar, tooltips: tooltips.length > 0 ? tooltips : null };
}

export const defaultActbarIcons = { show: 'chevrons-right', hide: 'chevrons-right' };
export async function hideActionBar(
    actionBar: HTMLCalciteActionBarElement,
    icons: { show: string, hide: string } = defaultActbarIcons
) {
    const actions = actionBar.querySelectorAll('calcite-action');
    actionBar.querySelectorAll('calcite-action').forEach((act) => {
        if (act.id === defaultHideBtnId) return;
        act.hidden = !act.hidden;
    });
    const hideBtn = (actionBar.querySelector(`#${defaultHideBtnId}`) as HTMLCalciteActionElement);
    hideBtn.icon = [...actions].some((a) => a.hidden) ? icons.show : icons.hide;
}

export type actBarsProps = {
    cssClass?: string;
    meta: Array<[string, () => actbarWithTooltips]>;
    parent: HTMLElement & { tooltips: HTMLCalciteTooltipElement[] };
};
export function initActionBars(props: actBarsProps): HTMLDivElement {
    const container = document.createElement('div');
    if (props.cssClass) container.classList.add(props.cssClass);
    for (const [bar, fn] of props.meta) {
        const actBar = fn();
        props.parent[bar] = actBar.bar;
        container.appendChild(props.parent[bar]);
        props.parent.tooltips.push(...actBar.tooltips);
    }
    return container;
}
// to be passed as a click listener for calcite actions in an action bar. opens a calcite panel on click
export function toggleActionPanel(
    id: string, actionBar: HTMLCalciteActionBarElement,
    panels: Record<string, HTMLElement>
): void {
    actionBar.querySelectorAll("calcite-action").forEach((a: HTMLCalciteActionElement) => {
        a.active = a.dataset['actionId'] === id ? !a.active : false;
    });
    Object.entries(panels).forEach(([key, panel]: [string, any]) => {
        panel.hidden = key !== id || !(actionBar.querySelector(`[data-action-id="${id}"]`) as any).active;
        if (!panel.hidden) panel.closed = false;
    });
}

export function buildCalciteLegendPanel(heading: string): HTMLCalcitePanelElement {
    const panel = document.createElement("calcite-panel");
    panel.heading = heading;
    panel.hidden = true;
    const content = document.createElement('arcgis-legend');
    panel.append(content);
    return panel;
}

export type calciteSliderBlock = {
    heading: string,
    sliderProps: Partial<HTMLCalciteSliderElement>,
    onInput: (val: number) => Promise<void>,
    cssClass?: string,
    noticeTxt?: string,
};
export function buildCalciteSliderBlock(props: calciteSliderBlock): { block: HTMLCalciteBlockElement, slider: HTMLCalciteSliderElement } { 
    const block = Object.assign(document.createElement('calcite-block'), {
        heading: props.heading,
        // open: true,
        expanded: true,
        className: props.cssClass ?? '',
    });
    const slider = Object.assign(document.createElement('calcite-slider'), { ...props.sliderProps });
    slider.addEventListener('calciteSliderInput', async () => props.onInput(slider.value as number));
    block.append(slider);
    return { block, slider};
};

// build a calcite block that accepts a function to built a table inside the block
export function buildCalciteTableBlock(
    label: string, props: any, collapsible: boolean, open: boolean,
    buildTable: (props: unknown) => HTMLCalciteTableElement,
    infoContent?: string
): HTMLCalciteBlockElement {
    const block = Object.assign(document.createElement('calcite-block'), {
        heading: label,
        label: label,
        collapsible: collapsible,
        expanded: open,
        headingLevel: 2
    });

    if (infoContent) {
        const notice = buildCalciteNotice({ label, content: infoContent });
        block.append(notice.btn, notice.notice);
    }

    block.append(buildTable(props));
    return block;
}

// table builder helper
export type calciteTableProps = {
    hasHeader: boolean,
    rows: any[][],
}
// if the table has a header row, pass it as the first row and set hasHeader to true
export function buildCalciteTable(props: calciteTableProps): HTMLCalciteTableElement {
    if (!props.rows.length) throw new Error(`no rows passed`);

    // all rows must respect this length
    const numCols = props.rows[0]?.length;

    // build a row for each
    const rows: Array<HTMLCalciteTableRowElement> = [];
    props.rows.forEach((r: any[], i: number) => { 
        if (r.length !== numCols) {
            throw new Error(
                `number of values (${r.length}) in row array ${i + 1}/${rows.length} does not match the numCols variable (${numCols})`
            );
        }
        rows.push(document.createElement('calcite-table-row'));
    });

    // if hasHeader, set first row as the a slotted header element 
    if (props.hasHeader) {
        const headerRow = props.rows?.[0];
        headerRow?.forEach((r: any) => {
            const row = rows[0];
            if (!row) return;
            row.slot = 'table-header';
            row.append(Object.assign(document.createElement('calcite-table-header'), { heading: String(r) }));
        });
    }

    // append each val in each row as a cell in the table
    // start at row 0 if no header, row 1 if hasHeader
    const cellRows = (props.hasHeader) ? rows.slice(1) : rows;
    cellRows.forEach((r: HTMLCalciteTableRowElement, i: number) => {
        const rowIdx = props.hasHeader ? i + 1 : i;
        const row = props.rows?.[rowIdx];
        row?.forEach((val: any) => {
            r.append(Object.assign(document.createElement('calcite-table-cell'), { innerText: String(val) ?? 'NA' }));
        });
    });

    // create table and append all rows
    const tbl = document.createElement("calcite-table");
    tbl.append(...rows);
    return tbl;
}

// calcite notice with info button
// closable block for medium amounts of text, naturally expands its container
export function buildCalciteNotice(props: { label: string, content: string }): {
    notice: HTMLCalciteNoticeElement, btn: HTMLCalciteActionElement
} {
    const actId = `popover-trigger-${props.label.replace(/\s+/g, '-')}`;
    const btn = Object.assign(document.createElement('calcite-action'), {
        slot: 'control',
        icon: 'information',
        text: 'Info',
        id: actId,
        scale: 'm',
    });

    const notice = Object.assign(document.createElement('calcite-notice'), {
        expanded: false,
        // open: false,
        kind: 'info',
        scale: 's',
        closable: true,
    });

    // toggle notice on button click
    btn.addEventListener('click', () => {
        notice.expanded = !notice.expanded;
        notice.open = !notice.open;
    });
    
    notice.appendChild(Object.assign(document.createElement('div'), {
        slot: 'message',
        innerText: props.content,
    }));

    return {notice: notice, btn: btn};
}



// build a single calcite button
export function buildCalciteButton(props: { txt: string, appearance?: string, scale?: string, icon?: string }): HTMLCalciteButtonElement {
    const btn = Object.assign(document.createElement("calcite-button"), {
        textContent: props.txt.trim(),
        appearance: props.appearance?.trim() ?? "outline",
        scale: props.scale?.trim() ?? "s",
    });
    btn.setAttribute("appearance", "outline");
    btn.setAttribute("scale", "s");
    if (props.icon) btn.iconStart = props.icon;
    return btn;
}
export function makeRoutesButtons(routeNames: string,
    onRouteClick: (route: string) => void,
    onRoutesClick: (route: string | string[]) => void
): HTMLCalciteButtonElement[] {
    let routeBtns: HTMLCalciteButtonElement[] = [];
    if (routeNames) {
        routeNames.split(", ").forEach((route: string) => {
            if (route.includes("No bus stop")) return;
            const btn = buildCalciteButton({ txt: route });
            btn.addEventListener("click", () => onRouteClick(route.trim()));
            routeBtns.push(btn);
        });
        if (routeBtns.length > 1) {
            const allBtn = buildCalciteButton({ txt: "Highlight Each" });
            allBtn.addEventListener("click", () => {
                const routes = routeNames.split(", ").map(r => r.trim());
                onRoutesClick(routes);
            });
            routeBtns.push(allBtn)
        }
    }
    return routeBtns;
}

// calcite select helpers
export type calciteOptionProps = {
    value: string,
    label: string,
}
export type calciteSelectProps = {
    heading: string,
    onSelChange: (val: string) => void,
    cssClass?: string,
    optsProps?: {
        allOpt?: calciteOptionProps,
        opts?: calciteOptionProps[],
        dataUrl?: string,
        mapFeatures?: (features: any[]) => any[],
    },
}
export async function buildCalciteSelect(props: calciteSelectProps): Promise<HTMLCalciteSelectElement> {
    const sel = Object.assign(document.createElement('calcite-select'), {
        heading: props.heading,
        label: props.heading,
    });
    if (props.cssClass) sel.classList.add(props.cssClass);
    sel.addEventListener('calciteSelectChange', () => props.onSelChange(sel.value));
    
    const builtOpts: HTMLCalciteOptionElement[] = [];
    
    if (props.optsProps) {    
        // all option
        if (props.optsProps.allOpt) {
            builtOpts.push(Object.assign(document.createElement('calcite-option'), {
                label: props.optsProps.allOpt.label,
                value: props.optsProps.allOpt.value,
            }));
        }
        if (props.optsProps.dataUrl) {
            try {
                const data = await fetch(props.optsProps.dataUrl).then(r => r.json());
                if (props.optsProps.mapFeatures) {
                    const opts = props.optsProps.mapFeatures(data.features);
                    for (const opt of opts) {
                        builtOpts.push(Object.assign(document.createElement('calcite-option'), {
                            label: opt,
                            value: opt,
                        }));
                    }
                } else {
                    throw new Error(`must pass a function to optsProps.mapFeatures if optsPropts.dataUrl is set`);
                }
                
            } catch (e) {
                throw new Error(`failed to fetch data from ${props.optsProps.dataUrl}: ${e}`);
            }
        } else {
            if (!props.optsProps.opts) throw new Error('optsProps.opts must be passed if optsProps.dataUrl is not set');
            for (const opt of props.optsProps.opts) {
                builtOpts.push(Object.assign(document.createElement('calcite-option'), {
                    label: opt.label,
                    value: opt.value,
                }));
            }
        }
        if (builtOpts.length === 0) {
            throw new Error('no options');
        }
        sel.append(...builtOpts);
    }
    return sel;
}

export type calciteSelectBlock = {
    heading: string,
    selProps: calciteSelectProps,
    onSelChange?: (vals: string[]) => void,
    cssClass?: string,
};
export async function buildCalciteSelectBlock(props: calciteSelectBlock): Promise<HTMLCalciteBlockElement> {
    const block = Object.assign(document.createElement('calcite-block'), {
        heading: props.heading,
        expanded: true,
        className: props.cssClass ?? '',
    });
    const sel = await buildCalciteSelect(props.selProps);
    block.append(sel);
    return block;
} 

export type calciteDropdownProps = {
    heading: string,
    onSelChange: (vals: string[]) => void,
    cssClass?: string,
    placeholder?: string,
    icon?: string,
    selectMode?: 'single' | 'single-persist' | 'multiple',
    optsProps?: {
        allOpt?: calciteOptionProps,
        dataUrl?: string,
        mapFeatures: (features: any[]) => any[],
    },
}

// rather than combobox, create a dropdown with button and dropdown group selection multiple
export async function buildCalciteDropdown(props: calciteDropdownProps, clearBtn?: boolean): Promise<HTMLCalciteDropdownElement> {
    const down = Object.assign(document.createElement('calcite-dropdown'), {
        width: 'm',
        closeOnSelectDisabled: true,
    });
    if (props.cssClass) down.classList.add(props.cssClass);

    const btn = Object.assign(buildCalciteButton({txt: props.heading}), { slot: 'trigger' });
    if (props.icon) btn.iconStart = props.icon;
    const dropGroup = Object.assign(document.createElement('calcite-dropdown-group'), {
        groupTitle: props.heading,
        selectionMode: props.selectMode,
    });

    if (props.optsProps) {
        let builtOpts: HTMLCalciteDropdownItemElement[] = [];

        if (props.optsProps.dataUrl) {
            try {
                const data = await fetch(props.optsProps.dataUrl).then(r => r.json());
                const opts = props.optsProps.mapFeatures(data.features);
                for (const opt of opts) {
                    builtOpts.push(Object.assign(document.createElement('calcite-dropdown-item'), {
                        label: opt,
                        textContent: opt,
                    }));
                }
            } catch (e) {
                throw new Error(`failed to fetch data from ${props.optsProps.dataUrl}: ${e}`);
            }
        }
        if (builtOpts.length === 0) {
            throw new Error('no options');
        }
        dropGroup.append(...builtOpts);
    }
    down.addEventListener('calciteDropdownSelect', () => {
        const vals = Array.from(dropGroup.querySelectorAll('calcite-dropdown-item'))
            .filter((i: any) => i.selected)
            .map((i: any) => i.textContent);
        console.log(vals);
        props.onSelChange(vals);    
    });

    if (clearBtn) {
        const clearBtn = buildCalciteButton({ txt: '' });
        clearBtn.slot = 'trigger';
        clearBtn.iconStart = 'reset';
        clearBtn.addEventListener('click', () => {
            dropGroup.querySelectorAll('calcite-dropdown-item').forEach((item: HTMLCalciteDropdownItemElement) => item.selected = false);
            props.onSelChange(["*"]);
        })
        down.append(clearBtn);
    }
    
    down.append(btn, dropGroup);
    return down;
}